import React from 'react';
import { motion } from 'framer-motion';
import { Wifi, Star, Zap, Gamepad, Video, Briefcase, Laptop } from 'lucide-react';
import { useWirelessAdvisor } from '../../context/WirelessAdvisorContext';
import { formatRoomName } from '../../utils/formatters';
import QuestionTooltip from '../shared/QuestionTooltip';
import { trackEvent } from '../../../../utils/analytics';

/**
 * Coverage component
 * Fifth step in the wireless advisor form
 * Allows users to specify their coverage priorities
 */
const Coverage = () => {
  const { formData, updateFormData } = useWirelessAdvisor();
  
  // Handle priority room selection
  const handlePriorityRoomSelect = (roomType) => {
    // Toggle the room in the priority list
    const currentPriorities = formData.priorityRooms || [];
    let newPriorities;
    
    if (currentPriorities.includes(roomType)) {
      newPriorities = currentPriorities.filter(r => r !== roomType);
    } else {
      newPriorities = [...currentPriorities, roomType];
    }
    
    // Update form data
    updateFormData({
      priorityRooms: newPriorities
    });
    
    // Track event
    trackEvent('wireless_advisor_priority_room_selected', {
      room_type: roomType,
      is_selected: !currentPriorities.includes(roomType)
    });
  };
  
  // Handle activity selection
  const handleActivitySelect = (activity) => {
    // Toggle the activity in the list
    const currentActivities = formData.priorityActivities || [];
    let newActivities;
    
    if (currentActivities.includes(activity)) {
      newActivities = currentActivities.filter(a => a !== activity);
    } else {
      newActivities = [...currentActivities, activity];
    }
    
    // Update form data
    updateFormData({
      priorityActivities: newActivities
    });
    
    // Track event
    trackEvent('wireless_advisor_priority_activity_selected', {
      activity,
      is_selected: !currentActivities.includes(activity)
    });
  };
  
  // Get rooms with counts > 0
  const getPopulatedRooms = () => {
    return Object.entries(formData.rooms || {})
      .filter(([_, count]) => count > 0)
      .map(([roomType]) => roomType);
  };
  
  // Common activities that need good WiFi
  const activities = [
    { id: 'streaming', label: 'Video Streaming', icon: <Video className="h-5 w-5" />, description: '4K streaming, Netflix, YouTube' },
    { id: 'gaming', label: 'Online Gaming', icon: <Gamepad className="h-5 w-5" />, description: 'Fast-paced games, low latency needs' },
    { id: 'videoCall', label: 'Video Calls', icon: <Laptop className="h-5 w-5" />, description: 'Zoom, Teams, video conferencing' },
    { id: 'work', label: 'Work From Home', icon: <Briefcase className="h-5 w-5" />, description: 'Remote work, file uploads/downloads' },
    { id: 'smartHome', label: 'Smart Home', icon: <Zap className="h-5 w-5" />, description: 'IoT devices, smart speakers, cameras' }
  ];
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Coverage Priorities</h2>
        <QuestionTooltip 
          content="Tell us which rooms and activities need the best WiFi coverage. This helps us optimize your wireless setup."
          iconSize="md"
        />
      </div>
      
      <p className="text-gray-400 text-sm">
        Select the rooms and activities that need the strongest and most reliable WiFi connection.
      </p>
      
      {/* Priority Rooms */}
      <div className="space-y-3">
        <div className="flex items-center">
          <label className="block text-sm font-medium text-gray-300 mr-2">
            Which rooms need the best WiFi coverage?
          </label>
          <QuestionTooltip 
            content="Select up to 3 rooms that are most important for strong WiFi. These will be prioritized in our recommendations."
            iconSize="sm"
          />
        </div>
        
        {getPopulatedRooms().length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {getPopulatedRooms().map((roomType) => {
              const isSelected = (formData.priorityRooms || []).includes(roomType);
              
              return (
                <motion.button
                  key={roomType}
                  onClick={() => handlePriorityRoomSelect(roomType)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-purple-600 bg-purple-600/10'
                      : 'border-gray-700 hover:border-purple-500/50 bg-gray-800/50'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={(formData.priorityRooms || []).length >= 3 && !isSelected}
                >
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-purple-600/20' : 'bg-gray-700'} mr-3`}>
                      {isSelected ? (
                        <Star className="h-5 w-5 text-purple-400" />
                      ) : (
                        <Wifi className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <span className={`font-medium ${isSelected ? 'text-purple-400' : 'text-white'}`}>
                      {formatRoomName(roomType)}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        ) : (
          <div className="p-4 bg-gray-800 rounded-lg">
            <p className="text-gray-400 text-sm">
              No rooms have been added yet. Please go back to the Rooms step to add rooms to your home.
            </p>
          </div>
        )}
        
        {(formData.priorityRooms || []).length >= 3 && (
          <p className="text-xs text-amber-400">
            You've selected the maximum of 3 priority rooms.
          </p>
        )}
      </div>
      
      {/* Priority Activities */}
      <div className="space-y-3">
        <div className="flex items-center">
          <label className="block text-sm font-medium text-gray-300 mr-2">
            What activities do you do that need good WiFi?
          </label>
          <QuestionTooltip 
            content="Different activities have different WiFi requirements. This helps us optimize your network for your specific needs."
            iconSize="sm"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {activities.map((activity) => {
            const isSelected = (formData.priorityActivities || []).includes(activity.id);
            
            return (
              <motion.button
                key={activity.id}
                onClick={() => handleActivitySelect(activity.id)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-purple-600 bg-purple-600/10'
                    : 'border-gray-700 hover:border-purple-500/50 bg-gray-800/50'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-purple-600/20' : 'bg-gray-700'} mr-3`}>
                    {React.cloneElement(activity.icon, { 
                      className: `h-5 w-5 ${isSelected ? 'text-purple-400' : 'text-gray-400'}`
                    })}
                  </div>
                  <div className="text-left">
                    <span className={`font-medium ${isSelected ? 'text-purple-400' : 'text-white'}`}>
                      {activity.label}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">
                      {activity.description}
                    </p>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
      
      {/* Summary */}
      {((formData.priorityRooms && formData.priorityRooms.length > 0) || 
         (formData.priorityActivities && formData.priorityActivities.length > 0)) && (
        <motion.div
          className="mt-6 p-4 bg-purple-600/10 border border-purple-600/20 rounded-lg"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="text-lg font-medium text-purple-400 mb-2">
            Coverage Priorities Summary
          </h3>
          
          {formData.priorityRooms && formData.priorityRooms.length > 0 && (
            <div className="mb-3">
              <h4 className="text-sm font-medium text-gray-300 mb-1">Priority Rooms:</h4>
              <div className="flex flex-wrap gap-2">
                {formData.priorityRooms.map((roomType) => (
                  <div key={roomType} className="bg-purple-600/20 text-purple-300 px-2 py-1 rounded-full text-xs flex items-center">
                    <Star className="h-3 w-3 mr-1" />
                    {formatRoomName(roomType)}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {formData.priorityActivities && formData.priorityActivities.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-1">Priority Activities:</h4>
              <div className="flex flex-wrap gap-2">
                {formData.priorityActivities.map((activityId) => {
                  const activity = activities.find(a => a.id === activityId);
                  return (
                    <div key={activityId} className="bg-purple-600/20 text-purple-300 px-2 py-1 rounded-full text-xs flex items-center">
                      {React.cloneElement(activity.icon, { className: "h-3 w-3 mr-1" })}
                      {activity.label}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default Coverage;